import { ArrowDropDown, ExitToApp, GetApp, Settings } from "@mui/icons-material"
import {
    AppBar,
    Box,
    Button,
    Container,
    FormControl,
    Grid,
    Menu,
    MenuItem,
    MenuList,
    Select,
    SelectChangeEvent,
    Toolbar,
    Typography,
} from "@mui/material"
import React, { useState } from "react"
import { Link } from "react-router-dom"
import { useToggle } from "../../hooks/useToggle"
import { routes } from "../../routing/NexRoutes"
import { OptionType } from "../../store/types/OptionType"
import { getDistanceUnits, getHeightUnits } from "../../utils/UnitCalculations"
import { useAuthStore } from "../../zstore/AuthStore"
import { DistanceUnits, HeightUnits, TimeConvention, useLocalStorageStore } from "../../zstore/LocalStorageStore"
import { useMasterLogStore } from "../../zstore/MasterLogStore"
import { AlertModal } from "../Modals/AlertModal"
import { Tool } from "../Tool/Tool"
import { useStyles } from "./styles"

const jumpToFields: { label: string; link: string }[] = [
    { label: "Discover", link: "https://discover.nv5geospatial.com" },
    { label: "Discover (Test)", link: "https://discover-test.nv5geospatial.com/" },
    {
        label: "User Guides",
        link: "https://nv5inc.sharepoint.com/sites/NexusUserGroup/SitePages/Nexus-User-Guide.aspx",
    },
    {
        label: "User Group",
        link: "https://teams.microsoft.com/l/team/19:qHbVnWc-0O7dITRh90ysuWthYv9U5kVVi1z5DYf4ulk1%40thread.tacv2/conversations?groupId=4e247e63-8bcc-4d93-b836-26ec486e5253",
    },
]

// slower tooltip
const TooltipProps = {
    enterDelay: 400,
    enterNextDelay: 100,
}

export const Header = () => {
    const { classes } = useStyles()
    const downloadMasterLog = useMasterLogStore((state) => state.downloadMasterLog)
    const isMasterLogDownloading = useMasterLogStore((state) => state.downloading)
    const userLoading = useAuthStore((state) => state.userLoading)
    const user = useAuthStore((state) => state.user)
    const checkNV5GClientAccess = useAuthStore((state) => state.checkNV5GClientAccess)
    const logout = useAuthStore((state) => state.logout)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>()
    const [prefModalOpen, togglePrefModalOpen] = useToggle(false)

    const timeConvention = useLocalStorageStore((state) => state.timeConvention)
    const setTimeConvention = useLocalStorageStore((state) => state.setTimeConvention)
    const distanceUnits = useLocalStorageStore((state) => state.preferredDistanceUnit)
    const setDistanceUnits = useLocalStorageStore((state) => state.setPreferredDistanceUnit)
    const heightUnit = useLocalStorageStore((state) => state.preferredHeightUnit)
    const setHeightUnit = useLocalStorageStore((state) => state.setPreferredHeightUnit)

    const downloadOptions: OptionType[] = [{ id: "0", label: "Download Master Log", disabled: isMasterLogDownloading }]

    const handleDownloadTool = async (option: OptionType) => {
        if (typeof option === "object") {
            switch (option.id) {
                case "0":
                    await downloadMasterLog()
                    break
                default:
                    break
            }
        }
    }

    const handleClick = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
    const id = anchorEl ? "simple-popover" : undefined

    return (
        <>
            <AppBar position="fixed">
                <Container maxWidth="md">
                    <Toolbar disableGutters>
                        <Grid container justifyContent="space-between">
                            {/* Uploader Title and Link selection */}
                            <Grid item className={classes.header_action_group}>
                                <Typography component={Link} to={routes.home} variant="h6" className={classes.title}>
                                    Uploader
                                </Typography>
                                <FormControl variant="standard" sx={{ m: 2, ml: 4, minWidth: 130 }}>
                                    <Button
                                        sx={{ alignItems: "center" }}
                                        disableRipple
                                        className={classes.linksButton}
                                        aria-describedby={id}
                                        onClick={(e) => handleClick(e)}
                                    >
                                        <Typography variant="body1">Jump to...</Typography>
                                        <ArrowDropDown />
                                    </Button>
                                    <Menu
                                        id={id}
                                        open={!!anchorEl}
                                        onClose={() => setAnchorEl(null)}
                                        anchorEl={anchorEl}
                                    >
                                        <MenuList sx={{ maxWidth: 150 }}>
                                            {jumpToFields.map((jump, idx) => {
                                                return (
                                                    <a key={idx} href={jump.link} className={classes.linkName}>
                                                        <MenuItem>{jump.label}</MenuItem>
                                                    </a>
                                                )
                                            })}
                                        </MenuList>
                                    </Menu>
                                </FormControl>
                            </Grid>
                            {/* Download and Logout */}
                            <Grid item className={classes.header_action_group}>
                                {!userLoading && user != null && (
                                    <Grid container sx={{ gap: 2 }}>
                                        <Tool
                                            color="white"
                                            tooltip="Preferences"
                                            Icon={Settings}
                                            onChange={togglePrefModalOpen}
                                        />
                                        {checkNV5GClientAccess() && (
                                            <Tool
                                                tooltip="Download Reports"
                                                color="white"
                                                options={downloadOptions}
                                                Icon={GetApp}
                                                onChange={(option) => option && handleDownloadTool(option)}
                                                TooltipProps={TooltipProps}
                                            />
                                        )}
                                        <Tool
                                            tooltip="Logout"
                                            color="white"
                                            Icon={ExitToApp}
                                            onChange={logout}
                                            TooltipProps={TooltipProps}
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Toolbar>
                </Container>
            </AppBar>
            {/* fill space under fixed AppBar */}
            <AlertModal onClose={togglePrefModalOpen} title={"Preferences"} open={prefModalOpen} declineText="Close">
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, m: 2 }}>
                    <Select
                        labelId="distunits-select-label"
                        id="distunits-select"
                        value={distanceUnits}
                        onChange={(e: any) => setDistanceUnits(e.target.value as DistanceUnits)}
                        renderValue={(value) => `Distance (${value})`}
                    >
                        {getDistanceUnits().map((u, idx) => (
                            <MenuItem key={idx} value={u.abbr}>
                                {u.plural}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        labelId="heightunits-select-label"
                        id="heightunits-select"
                        value={heightUnit}
                        onChange={(e: any) => setHeightUnit(e.target.value as HeightUnits)}
                        renderValue={(value) => `Height (${value})`}
                    >
                        {getHeightUnits().map((u, idx) => (
                            <MenuItem key={idx} value={u.abbr}>
                                {u.plural}
                            </MenuItem>
                        ))}
                    </Select>
                    <Select
                        labelId="time-format-select-label"
                        id="time-format-select"
                        value={timeConvention}
                        onChange={(e: SelectChangeEvent<TimeConvention>) =>
                            setTimeConvention(e.target.value as TimeConvention)
                        }
                    >
                        <MenuItem value={TimeConvention.TWELVE}>12 hour</MenuItem>
                        <MenuItem value={TimeConvention.TWENTY_FOUR}>24 hour</MenuItem>
                    </Select>
                </Box>
            </AlertModal>
            <Toolbar />
        </>
    )
}
